import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CentresList() {
  const token = localStorage.getItem('vendor_token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const [centres, setCentres] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editCentre, setEditCentre] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', phone: '' });

  const fetchCentres = useCallback(async () => {
    const res = await fetch(`${API}/api/lis/centres`, { headers: { Authorization: `Bearer ${token}` } });
    setCentres(await res.json());
  }, [token]);
  useEffect(() => { fetchCentres(); }, [fetchCentres]);

  const openCreate = () => { setEditCentre(null); setForm({ name: '', address: '', phone: '' }); setShowDialog(true); };
  const openEdit = (c) => { setEditCentre(c); setForm({ name: c.name, address: c.address || '', phone: c.phone || '' }); setShowDialog(true); };
  const save = async () => {
    const url = editCentre ? `${API}/api/lis/centres/${editCentre.id}` : `${API}/api/lis/centres`;
    const res = await fetch(url, { method: editCentre ? 'PUT' : 'POST', headers, body: JSON.stringify(form) });
    if (res.ok) { toast.success(editCentre ? 'Updated' : 'Created'); setShowDialog(false); fetchCentres(); } else toast.error('Failed');
  };
  const deleteCentre = async (id) => { if (!window.confirm('Delete?')) return; await fetch(`${API}/api/lis/centres/${id}`, { method: 'DELETE', headers }); toast.success('Deleted'); fetchCentres(); };

  return (
    <div data-testid="lis-centres-list">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-slate-800">Collection Centres</h1>
        <Button size="sm" onClick={openCreate} data-testid="add-centre-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Centre</Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {centres.map(c => (
          <Card key={c.id} className="border-slate-200"><CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex gap-3"><div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0"><Building2 className="w-4.5 h-4.5 text-amber-600" /></div>
                <div><p className="font-medium text-slate-800">{c.name}</p>{c.phone && <p className="text-xs text-slate-500">{c.phone}</p>}{c.address && <p className="text-xs text-slate-400">{c.address}</p>}</div></div>
              <div className="flex gap-1"><button onClick={() => openEdit(c)} className="p-1 text-slate-400 hover:text-blue-600"><Pencil className="w-3.5 h-3.5" /></button><button onClick={() => deleteCentre(c.id)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button></div>
            </div>
          </CardContent></Card>
        ))}
        {centres.length === 0 && <Card className="border-dashed border-slate-300 col-span-full"><CardContent className="p-8 text-center text-slate-400"><Building2 className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No collection centres yet.</p></CardContent></Card>}
      </div>
      <Dialog open={showDialog} onOpenChange={setShowDialog}><DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>{editCentre ? 'Edit Centre' : 'Add Collection Centre'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Centre Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="centre-name-input" />
          <Input placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
          <Input placeholder="Address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button><Button onClick={save} data-testid="save-centre-btn">{editCentre ? 'Update' : 'Create'}</Button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}
